import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, PlaySquare, Images, Radio, Crown, User, Users, ShieldCheck, Clock, LayoutDashboard } from "lucide-react";
import { DevPreviewBanner } from "@/components/DevPreviewBanner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export function ReviewerBanner() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const isReviewer = user?.role === "external_premium_reviewer";
  useEffect(() => {
    if (isReviewer) api.get("/reviewer/me").then((r) => setInfo(r.data)).catch(() => {});
  }, [isReviewer]);
  if (!isReviewer) return null;
  return (
    <div data-testid="reviewer-banner" className="sticky top-0 z-50 flex items-center gap-1.5 border-b border-[#a03cff]/40 bg-[#140b20]/95 px-4 py-2 text-xs text-[#e0c2ff] backdrop-blur">
      <Clock size={13} className="shrink-0" />
      <span>Reviewer access{info ? ` for ${info.organization} · until ${new Date(info.expires_at).toLocaleString()}` : ""} · read-only, no payments</span>
    </div>
  );
}

export function OwnerReviewBanner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user?.role !== "platform_owner_review") return null;
  const toAdmin = async () => {
    navigate("/login?switch=admin", { replace: true });
    await logout();
  };
  return (
    <div data-testid="owner-review-banner" className="sticky top-0 z-50 flex items-center justify-between gap-2 border-b border-[#d42be8]/40 bg-[#1a0b20]/95 px-4 py-2 text-xs text-[#f0a8fa] backdrop-blur">
      <span className="flex items-center gap-1.5"><ShieldCheck size={13} /> Owner Review · all content unlocked · views are logged</span>
      <button onClick={toAdmin} data-testid="switch-to-admin-btn" className="rounded-full border border-[#d42be8]/50 px-3 py-1 font-semibold">Super Admin Dashboard</button>
    </div>
  );
}

export function CreatorViewBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user?.role !== "creator") return null;
  return (
    <div data-testid="creator-view-banner" className="sticky top-0 z-50 flex items-center justify-between gap-2 border-b border-[#d42be8]/40 bg-[#1a0b20]/95 px-4 py-2 text-xs text-[#f0a8fa] backdrop-blur">
      <span>Subscriber view · browsing as a fan</span>
      <button onClick={() => navigate("/creator/dashboard")} data-testid="back-to-studio-btn" className="flex items-center gap-1 rounded-full border border-[#d42be8]/50 px-3 py-1 font-semibold"><LayoutDashboard size={12} /> Creator Studio</button>
    </div>
  );
}

export const NAV = [
  { to: "/app/discover", label: "Discover", icon: Home },
  { to: "/app/creators", label: "Creators", icon: Users },
  { to: "/app/videos", label: "Videos", icon: PlaySquare },
  { to: "/app/photos", label: "Photos", icon: Images },
  { to: "/app/live", label: "Live", icon: Radio },
  { to: "/app/membership", label: "Membership", icon: Crown },
  { to: "/app/profile", label: "Profile", icon: User },
];

export function SubscriberLayout() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <DevPreviewBanner />
      <OwnerReviewBanner />
      <ReviewerBanner />
      <CreatorViewBanner />
      <main className="mx-auto max-w-md pb-24 min-h-screen relative">
        <Outlet key={pathname} />
      </main>
      <nav
        data-testid="subscriber-bottom-nav"
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40"
      >
        <div className="glass border-t border-white/10 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <ul className="flex items-center justify-between">
            {NAV.map(({ to, label, icon: Icon }) => (
              <li key={to} className="flex-1">
                <NavLink
                  to={to}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 py-1 text-[0.58rem] font-medium transition-colors ${
                      isActive ? "text-[#d42be8]" : "text-[#a7a0af] hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={20} strokeWidth={isActive ? 2.4 : 1.8} />
                      <span className="tracking-wide">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
